import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { QueryState } from '../components/page';
import { useIdentityEntities } from '../api/hooks';
import type { IdentityListParams } from '../api/types';
import { DataQualityHeader } from './DataQualityOverviewPage';
import {
  EntityFilters,
  EntityLink,
  PAGE_SIZE,
  Pager,
  loadError,
  placeText,
} from './medical-data-quality';

export function OrganisationsPage() {
  const [params, setParams] = useState<IdentityListParams>({
    sort: 'name',
    limit: PAGE_SIZE,
    offset: 0,
  });
  const organisations = useIdentityEntities('organisation', params);
  const page = organisations.data;
  const filtered = !!(params.q || params.city || params.flagged !== undefined);

  return (
    <div>
      <DataQualityHeader />
      <EntityFilters level="organisation" params={params} onChange={setParams} withCity />
      <QueryState isLoading={organisations.isLoading} error={loadError(organisations.error)}>
        <div className="overflow-hidden rounded-3xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organisation</TableHead>
                <TableHead className="text-right">Catalog Sites</TableHead>
                <TableHead className="text-right">Departments</TableHead>
                <TableHead className="text-right">Studies</TableHead>
                <TableHead className="text-right">Mentions</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {page?.items.map(organisation => (
                <TableRow key={organisation.id}>
                  <TableCell className="min-w-[16rem]">
                    <div className="font-medium">
                      <EntityLink kind="organisation" id={organisation.id}>
                        {organisation.display_name}
                      </EntityLink>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {placeText([organisation.city, organisation.country]) || 'No city'}
                      {organisation.external_ids.length > 0 &&
                        ` · ${organisation.external_ids.join(', ')}`}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{organisation.site_count}</TableCell>
                  <TableCell className="text-right">{organisation.department_count}</TableCell>
                  <TableCell className="text-right">{organisation.study_count}</TableCell>
                  <TableCell className="text-right">{organisation.mention_count}</TableCell>
                  <TableCell>
                    {organisation.open_flags > 0 ? (
                      <Badge variant="destructive">{organisation.open_flags} open</Badge>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {page?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="h-28 text-center text-muted-foreground">
                    {filtered
                      ? 'No Organisations match these filters.'
                      : 'No Organisations resolved yet.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {page && (
          <Pager
            total={page.total}
            limit={page.limit}
            offset={page.offset}
            onChange={offset => setParams(previous => ({ ...previous, offset }))}
          />
        )}
      </QueryState>
    </div>
  );
}
